import clsx from 'clsx';
import React, { type ForwardedRef } from 'react';

import {
    CardButton,
    type CardButtonProps,
    CardLink,
    type CardLinkProps,
    Heading,
    HeadingContext,
    type Href,
    type IconDefinition,
    renderIcon,
} from '@gitbook/spine-react';

export type QuickActionCardProps = {
    className?: string;
    href?: Href | string;
    children?: React.ReactNode;
} & Omit<CardButtonProps, 'children'> &
    Omit<CardLinkProps, 'children' | 'href'>;

/**
 * Quick action card component using composition.
 * Uses CardButton when there's no href, CardLink when there is.
 * Use QuickActionCardIcon and QuickActionCardHeading as children.
 */
export function QuickActionCard({
    ref,
    ...props
}: QuickActionCardProps & { ref?: React.Ref<HTMLDivElement | HTMLAnchorElement> }) {
    const { className, href, children, ...rest } = props;

    const cardClassName = clsx(
        'group/QAcard flex flex-col items-center justify-between gap-1 rounded-lg px-4 py-3',
        className
    );

    if (href) {
        // Handle external URLs (http/https)
        if (typeof href === 'string' && (href.startsWith('http') || href.startsWith('//'))) {
            return (
                <CardLink
                    ref={ref as ForwardedRef<HTMLAnchorElement>}
                    href={href}
                    className={cardClassName}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...(rest as CardLinkProps)}
                >
                    <HeadingContext.Provider value={{ level: 3, className: 'text-ui-base' }}>
                        {children}
                    </HeadingContext.Provider>
                </CardLink>
            );
        }

        // Handle navigation objects (Href type)
        return (
            <CardLink
                ref={ref as ForwardedRef<HTMLAnchorElement>}
                href={href as Href}
                className={cardClassName}
                {...(rest as CardLinkProps)}
            >
                <HeadingContext.Provider value={{ level: 3, className: 'text-ui-base' }}>
                    {children}
                </HeadingContext.Provider>
            </CardLink>
        );
    }

    return (
        <CardButton
            ref={ref as ForwardedRef<HTMLDivElement>}
            className={cardClassName}
            {...(rest as CardButtonProps)}
        >
            <HeadingContext.Provider value={{ level: 3, className: 'text-ui-base' }}>
                {children}
            </HeadingContext.Provider>
        </CardButton>
    );
}

export function QuickActionCardIcon(props: { children: IconDefinition }) {
    return (
        <div className="flex items-center justify-center rounded-lg border p-2">
            {renderIcon(props.children, {
                className: 'size-4 text-base',
            })}
        </div>
    );
}

export function SiteQuickActionCard({
    ref,
    ...props
}: CardButtonProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    return (
        <CardButton
            ref={ref}
            {...props}
            className={clsx(
                'flex flex-col items-center justify-center gap-3 rounded-md border border-solid border-base bg-base p-4 text-center',
                props.className
            )}
        >
            <HeadingContext.Provider value={{ level: 3, className: 'text-ui-base' }}>
                {props.children}
            </HeadingContext.Provider>
        </CardButton>
    );
}

export function QuickActionCardHeading(props: { children: React.ReactNode }) {
    return (
        <Heading className="text-center text-ui-base text-muted group-hover/QAcard:text-base">
            {props.children}
        </Heading>
    );
}
